import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Chip,
} from '@mui/material';
import {
  FileSpreadsheet,
  Upload,
  Trash2,
  FileText,
  Image,
  Archive,
  AlertCircle,
  X,
  ViewIcon,
} from 'lucide-react';
import CustomButton from '../../components/ui/CustomButton';
import { certificatesService } from '../../services/certificates';
import { useAuth } from '../../contexts/AuthContext';

// File type icons mapping
const getFileIcon = fileName => {
  // Add null/undefined checks
  if (!fileName || typeof fileName !== 'string') {
    return <FileSpreadsheet size={20} color="#6b7280" />;
  }

  const extension = fileName.split('.').pop()?.toLowerCase();
  if (!extension) {
    return <FileSpreadsheet size={20} color="#6b7280" />;
  }

  switch (extension) {
    case 'pdf':
      return <FileText size={20} color="#ef4444" />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
      return <Image size={20} color="#10b981" />;
    case 'zip':
    case 'rar':
    case '7z':
      return <Archive size={20} color="#f59e0b" />;
    default:
      return <FileSpreadsheet size={20} color="#6b7280" />;
  }
};

// Format file size
const formatFileSize = bytes => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Upload Dialog Component
const UploadDialog = ({
  open,
  onClose,
  onUpload,
  certificateId,
  isDarkMode,
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = event => {
    const files = Array.from(event.target.files);
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/zip',
      'application/x-zip-compressed',
    ];

    const validFiles = [];
    const errors = [];

    files.forEach(file => {
      if (!allowedTypes.includes(file.type)) {
        errors.push(`${file.name} has an unsupported type.`);
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        errors.push(`${file.name} exceeds 10MB size limit.`);
        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }

    setError(errors.join('\n'));
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) return;

    setUploading(true);
    setError('');

    try {
      for (const file of selectedFiles) {
        const attachmentData = {
          fileName: file.name,
          file: file,
          description: description.trim() || 'Supporting document',
        };

        await certificatesService.addAttachment(certificateId, attachmentData);
      }

      onUpload(); // Refresh attachments
      handleClose();
    } catch (error) {
      setError(error.message || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setDescription('');
    setError('');
    setUploading(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Upload size={24} color="#3b82f6" />
          <Typography
            variant="h6"
            sx={{
              ml: 1,
              fontWeight: 600,
              color: isDarkMode ? '#f1f5f9' : '#1e293b',
            }}
          >
            Upload Attachment
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.zip"
            multiple
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id="file-upload"
          />

          <label htmlFor="file-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<Upload size={16} />}
              fullWidth
              sx={{
                py: 2,
                borderColor: isDarkMode ? '#475569' : '#d1d5db',
                color: isDarkMode ? '#e2e8f0' : '#374151',
                borderStyle: 'dashed',
                '&:hover': {
                  borderColor: '#3b82f6',
                  backgroundColor: 'rgba(59, 130, 246, 0.05)',
                },
              }}
            >
              Choose File
            </Button>
          </label>

          {selectedFiles.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                border: '1px solid',
                borderColor: isDarkMode ? '#475569' : '#d1d5db',
                borderRadius: 1,
                backgroundColor: isDarkMode
                  ? 'rgba(30, 41, 59, 0.3)'
                  : 'rgba(249, 250, 251, 0.8)',
              }}
            >
              {selectedFiles.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getFileIcon(file.name)}
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: isDarkMode ? '#e2e8f0' : '#1f2937',
                        }}
                      >
                        {file.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: isDarkMode ? '#94a3b8' : '#6b7280' }}
                      >
                        {formatFileSize(file.size)}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setSelectedFiles(files =>
                        files.filter((_, i) => i !== index)
                      )
                    }
                    sx={{ color: isDarkMode ? '#94a3b8' : '#6b7280' }}
                  >
                    <X size={16} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <TextField
          fullWidth
          label="Description (Optional)"
          multiline
          rows={3}
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Enter a description for this attachment..."
          sx={{
            '& .MuiOutlinedInput-root': {
              color: isDarkMode ? '#e2e8f0' : '#1f2937',
              '& fieldset': {
                borderColor: isDarkMode ? '#475569' : '#d1d5db',
              },
              '&:hover fieldset': {
                borderColor: '#3b82f6',
              },
            },
            '& .MuiInputLabel-root': {
              color: isDarkMode ? '#94a3b8' : '#6b7280',
            },
          }}
        />

        <Box sx={{ mt: 2 }}>
          <Typography
            variant="caption"
            sx={{ color: isDarkMode ? '#94a3b8' : '#6b7280' }}
          >
            Supported formats: PDF, Images (JPG, PNG, GIF, WEBP), ZIP files
            <br />
            Maximum file size: 10MB
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <CustomButton
          variant="outlined"
          onClick={handleClose}
          disabled={uploading}
        >
          Cancel
        </CustomButton>
        <CustomButton
          variant="gradient"
          gradient="primary"
          onClick={handleUpload}
          disabled={!selectedFiles.length || uploading}
          startIcon={
            uploading ? <CircularProgress size={16} /> : <Upload size={16} />
          }
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

// Delete Confirmation Dialog
const DeleteConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  attachment,
  isDarkMode,
}) => {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      // Error handling should be done in parent component
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AlertCircle size={24} color="#ef4444" />
          <Typography
            variant="h6"
            sx={{
              ml: 1,
              fontWeight: 600,
              color: isDarkMode ? '#f1f5f9' : '#1e293b',
            }}
          >
            Delete Attachment
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ color: isDarkMode ? '#e2e8f0' : '#374151' }}>
          Are you sure you want to delete "{attachment?.fileName}"? This action
          cannot be undone.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <CustomButton variant="outlined" onClick={onClose} disabled={deleting}>
          Cancel
        </CustomButton>
        <CustomButton
          variant="outlined"
          onClick={handleDelete}
          disabled={deleting}
          startIcon={
            deleting ? <CircularProgress size={16} /> : <Trash2 size={16} />
          }
          sx={{
            borderColor: '#ef4444',
            color: '#ef4444',
            '&:hover': {
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              borderColor: '#dc2626',
            },
          }}
        >
          {deleting ? 'Deleting...' : 'Delete'}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

// Main Documents Tab Component
const DocumentsTab = ({ certificate, isDarkMode }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { isAdmin, isOffice } = useAuth();

  // Load attachments when component mounts
  useEffect(() => {
    if (certificate?.Id) {
      loadAttachments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificate?.Id]);

  const loadAttachments = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await certificatesService.getCertificateAttachments(
        certificate.Id
      );

      // Ensure data is array and handle potential undefined/null values
      const attachmentsArray = Array.isArray(data) ? data : [];
      // Filter out any invalid entries and ensure required properties exist
      const validAttachments = attachmentsArray
        .filter(
          attachment =>
            attachment &&
            typeof attachment === 'object' &&
            (attachment.fileName || attachment.FileName)
        )
        .map(attachment => {
          return {
            ...attachment,
            // Normalize property names using the actual API response structure
            fileName:
              attachment.FileName || attachment.fileName || 'Unknown File',
            id: attachment.Id || attachment.id || attachment.AttachmentId,
            description: attachment.Description || attachment.description || '',
            fileSize:
              attachment.FileSize || attachment.fileSize || attachment.Size,
            uploadDate:
              attachment.UploadedDate ||
              attachment.uploadDate ||
              attachment.CreatedDate,
            uploadedBy:
              attachment.UploadedBy ||
              attachment.uploadedBy ||
              attachment.CreatedBy,
            filePath: attachment.FilePath || attachment.filePath,
            fileType: attachment.FileType || attachment.fileType,
            originalFileName:
              attachment.OriginalFileName || attachment.originalFileName,
          };
        });

      setAttachments(validAttachments);
    } catch (error) {
      setError('Failed to load attachments');
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = () => {
    setSuccess('File uploaded successfully');
    setTimeout(() => setSuccess(''), 3000);
    loadAttachments();
  };

  const handleDelete = async () => {
    try {
      const attachmentId =
        selectedAttachment.Id ||
        selectedAttachment.id ||
        selectedAttachment.AttachmentId;

      if (!attachmentId) {
        setError('Attachment ID not found - cannot delete');
        setTimeout(() => setError(''), 3000);
        return;
      }

      await certificatesService.deleteAttachment(attachmentId);
      setSuccess('Attachment deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
      loadAttachments();
    } catch (error) {
      console.error('Delete error:', error);
      setError(
        `Failed to delete attachment: ${error.message || 'Unknown error'}`
      );
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDownload = async attachment => {
    try {
      // Prioritize OriginalFileName for the most accurate file name
      const fileName =
        attachment.OriginalFileName ||
        attachment.originalFileName ||
        attachment.FileName ||
        attachment.fileName ||
        'downloaded_file';
      const filePath = attachment.FilePath || attachment.filePath;

      if (!filePath) {
        setError('File path not found - cannot download');
        setTimeout(() => setError(''), 3000);
        return;
      }

      // Show loading state
      setSuccess('Processing file...');

      try {
        // Check if it's an image or PDF for viewing vs downloading
        const fileType = attachment.FileType || attachment.fileType || '';
        const isViewable =
          fileType.startsWith('image/') || fileType === 'application/pdf';

        // First try to fetch the file from the provided FilePath
        const response = await fetch(filePath, {
          method: 'GET',
          mode: 'cors', // Handle CORS if needed
        });

        if (!response.ok) {
          throw new Error(
            `Failed to access file: ${response.status} ${response.statusText}`
          );
        }

        // Get the blob from response
        const blob = await response.blob();

        // Create a blob with the correct MIME type
        const typedBlob = new Blob([blob], {
          type: fileType || blob.type || 'application/octet-stream',
        });

        // Create download link with proper filename
        const url = window.URL.createObjectURL(typedBlob);
        const link = document.createElement('a');
        link.href = url;

        // Use the original filename with proper extension
        link.download = fileName;

        // For viewable files, also provide option to view
        if (isViewable) {
          // Create a temporary link to view the file
          const viewLink = document.createElement('a');
          viewLink.href = url;
          viewLink.target = '_blank';
          viewLink.rel = 'noopener noreferrer';

          // Ask user preference for images and PDFs
          const userChoice = window.confirm(
            `This is a ${fileType.split('/')[0]} file. Would you like to:\n\n` +
              `• Click "OK" to VIEW the file in a new tab\n` +
              `• Click "Cancel" to DOWNLOAD the file\n\n` +
              `File: ${fileName}`
          );

          if (userChoice) {
            // User chose to view
            document.body.appendChild(viewLink);
            viewLink.click();
            document.body.removeChild(viewLink);
            setSuccess(`File opened for viewing: ${fileName}`);
          } else {
            // User chose to download
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setSuccess(`File downloaded: ${fileName}`);
          }
        } else {
          // For non-viewable files, directly download
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setSuccess(`File downloaded: ${fileName}`);
        }

        // Clean up the blob URL after a short delay
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);

        setTimeout(() => setSuccess(''), 3000);
      } catch (fetchError) {
        // If direct fetch fails, try opening in new tab as fallback
        console.warn(
          'Direct download failed, trying alternative method:',
          fetchError
        );

        // Try to open the file directly from the server
        const link = document.createElement('a');
        link.href = filePath;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';

        // Add download attribute with original filename
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setSuccess(`File opened in new tab: ${fileName}`);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Download error:', error);
      setError(
        `Failed to access file "${attachment.OriginalFileName || 'file'}": ${error.message}`
      );
      setTimeout(() => setError(''), 5000);
    }
  };

  const formatDate = dateString => {
    if (!dateString || typeof dateString !== 'string') return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  return (
    <Box>
      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Upload Section - Only for Office and Admin Users */}
      {(isOffice() || isAdmin()) && (
        <Box
          sx={{
            mb: 3,
            p: 3,
            background: isDarkMode
              ? 'rgba(30, 41, 59, 0.6)'
              : 'rgba(255, 255, 255, 0.8)',
            border: isDarkMode
              ? '1px solid rgba(148, 163, 184, 0.1)'
              : '1px solid rgba(148, 163, 184, 0.2)',
            borderRadius: 2,
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: isDarkMode ? '#f1f5f9' : '#1e293b',
                  mb: 1,
                }}
              >
                Upload New Attachment
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: isDarkMode ? '#94a3b8' : '#6b7280' }}
              >
                Add supporting documents to this certificate
              </Typography>
            </Box>
            <CustomButton
              variant="gradient"
              gradient="primary"
              startIcon={<Upload size={16} />}
              onClick={() => setUploadDialogOpen(true)}
            >
              Upload File
            </CustomButton>
          </Box>
        </Box>
      )}

      {/* Attachments List */}
      <Box
        sx={{
          background: isDarkMode
            ? 'rgba(30, 41, 59, 0.6)'
            : 'rgba(255, 255, 255, 0.8)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 3, pb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDarkMode ? '#f1f5f9' : '#1e293b',
              }}
            >
              Certificate Attachments
            </Typography>
            {attachments.length > 0 && (
              <Chip
                label={`${attachments.length} file${attachments.length !== 1 ? 's' : ''}`}
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, #3b82f620, #3b82f610)',
                  color: '#3b82f6',
                  border: '1px solid #3b82f630',
                }}
              />
            )}
          </Box>
        </Box>

        <Divider
          sx={{
            borderColor: isDarkMode
              ? 'rgba(148, 163, 184, 0.1)'
              : 'rgba(148, 163, 184, 0.2)',
          }}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : attachments.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <FileSpreadsheet
              size={48}
              color={isDarkMode ? '#64748b' : '#94a3b8'}
            />
            <Typography
              variant="h6"
              sx={{
                color: isDarkMode ? '#94a3b8' : '#64748b',
                mt: 2,
                mb: 1,
              }}
            >
              No Attachments
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: isDarkMode ? '#64748b' : '#94a3b8' }}
            >
              {(isOffice() || isAdmin())
                ? 'Upload files to attach supporting documents to this certificate.'
                : 'This certificate does not have any attachments.'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {attachments.map((attachment, index) => {
              // Additional safety check for each attachment
              if (!attachment || typeof attachment !== 'object') {
                return null;
              }

              // Ensure we have a valid fileName
              const fileName =
                attachment.fileName ||
                attachment.FileName ||
                `file_${index + 1}`;
              const fileId = attachment.id || attachment.Id || `temp_${index}`;

              return (
                <React.Fragment key={fileId}>
                  <ListItem
                    sx={{
                      py: 2,
                      px: 3,
                      '&:hover': {
                        backgroundColor: isDarkMode
                          ? 'rgba(59, 130, 246, 0.05)'
                          : 'rgba(59, 130, 246, 0.02)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 48 }}>
                      {getFileIcon(fileName)}
                    </ListItemIcon>

                    <ListItemText
                      primary={
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: isDarkMode ? '#e2e8f0' : '#1f2937',
                          }}
                        >
                          {fileName}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          {attachment.description && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: isDarkMode ? '#94a3b8' : '#6b7280',
                                mb: 0.5,
                              }}
                            >
                              {attachment.description}
                            </Typography>
                          )}
                          <Typography
                            variant="caption"
                            sx={{ color: isDarkMode ? '#64748b' : '#9ca3af' }}
                          >
                            {attachment.fileSize &&
                              formatFileSize(attachment.fileSize)}
                            {attachment.uploadDate &&
                              ` • Uploaded ${formatDate(attachment.uploadDate)}`}
                            {attachment.fileType && ` • ${attachment.fileType}`}
                            {attachment.uploadedBy &&
                              ` • by ${attachment.uploadedBy}`}
                          </Typography>
                        </Box>
                      }
                    />

                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Or Download Attachment">
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(attachment)}
                            sx={{
                              color: isDarkMode ? '#94a3b8' : '#64748b',
                              '&:hover': { color: '#3b82f6' },
                            }}
                            disabled={
                              !attachment.FilePath && !attachment.filePath
                            }
                          >
                            <ViewIcon size={18} />
                          </IconButton>
                        </Tooltip>

                        {(isOffice() || isAdmin()) && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedAttachment(attachment);
                                setDeleteDialogOpen(true);
                              }}
                              sx={{
                                color: isDarkMode ? '#94a3b8' : '#64748b',
                                '&:hover': { color: '#ef4444' },
                              }}
                              disabled={
                                !attachment.Id &&
                                !attachment.id &&
                                !attachment.AttachmentId
                              }
                            >
                              <Trash2 size={18} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>

                  {index < attachments.length - 1 && (
                    <Divider
                      sx={{
                        borderColor: isDarkMode
                          ? 'rgba(148, 163, 184, 0.05)'
                          : 'rgba(148, 163, 184, 0.1)',
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>

      {/* Upload Dialog */}
      <UploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onUpload={handleUploadSuccess}
        certificateId={certificate?.Id}
        isDarkMode={isDarkMode}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedAttachment(null);
        }}
        onConfirm={handleDelete}
        attachment={selectedAttachment}
        isDarkMode={isDarkMode}
      />
    </Box>
  );
};

export default DocumentsTab;
